import { useRef, useState, useEffect } from "react";
import { toPng } from "html-to-image";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Phone, Mail, MapPin, Download, ArrowLeft, Loader2, Globe, Plane, Clock, Heart } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { CONTACT } from "@/data/site";
import useSEO from "@/hooks/useSEO";

function CardFront() {
  return (
    <div
      className="relative w-[1050px] h-[600px] bg-[#0B132B] overflow-hidden"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* grid */}
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#8ea2c8 1px, transparent 1px), linear-gradient(90deg, #8ea2c8 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      {/* aurora */}
      <div className="absolute -top-40 -right-28 w-[460px] h-[460px] rounded-full" style={{ background: "rgba(60,74,114,0.6)", filter: "blur(100px)" }} />
      <div className="absolute -bottom-48 -left-28 w-[420px] h-[420px] rounded-full" style={{ background: "rgba(142,162,200,0.22)", filter: "blur(100px)" }} />

      {/* giant monogram watermark */}
      <div className="absolute -right-10 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none">
        <span className="font-display text-white leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: 620 }}>O</span>
      </div>

      {/* radial spotlight */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(80% 60% at 32% 45%, rgba(142,162,200,0.16) 0%, rgba(11,19,43,0) 60%)" }} />

      {/* ornate frame */}
      <div className="absolute inset-6 border border-white/12 rounded-[6px]" />
      <div className="absolute left-1/2 top-6 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rotate-45 bg-[#0B132B] border border-white/25" />
      <div className="absolute left-1/2 bottom-6 -translate-x-1/2 translate-y-1/2 w-3 h-3 rotate-45 bg-[#0B132B] border border-white/25" />

      <div className="relative z-10 h-full flex flex-col items-center justify-center px-16">
        {/* emblem with glow ring */}
        <div className="relative flex items-center justify-center">
          <div className="absolute w-[150px] h-[150px] rounded-full" style={{ background: "radial-gradient(circle, rgba(142,162,200,0.35) 0%, transparent 70%)" }} />
          <div className="relative w-[128px] h-[128px] rounded-full border border-white/15 flex items-center justify-center backdrop-blur-sm">
            <LogoMark size={96} />
          </div>
        </div>

        <div className="mt-9 flex items-baseline gap-3">
          <span className="font-display text-white text-[82px] leading-none tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>OKA</span>
          <span className="text-[22px] font-bold tracking-[0.35em] text-[#0B132B] bg-white px-3 py-1.5">VTC</span>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <span className="h-px w-14 bg-gradient-to-r from-transparent to-[#8ea2c8]/60" />
          <span className="text-[14px] tracking-[0.45em] uppercase text-white/60">Chauffeur privé d'exception</span>
          <span className="h-px w-14 bg-gradient-to-l from-transparent to-[#8ea2c8]/60" />
        </div>
      </div>

      {/* bottom info bar */}
      <div className="absolute bottom-10 left-0 right-0 flex items-center justify-center gap-4 text-[15px] tracking-[0.25em] text-[#8ea2c8]">
        <span>okavtc.com</span>
        <span className="w-1 h-1 rounded-full bg-[#8ea2c8]/60" />
        <span>{CONTACT.phoneDisplay}</span>
      </div>
    </div>
  );
}

function CardBack() {
  const rows = [
    { icon: Phone, label: "Téléphone", text: CONTACT.phoneDisplay },
    { icon: Mail, label: "Email", text: CONTACT.email },
    { icon: Globe, label: "Site web", text: "okavtc.com" },
    { icon: MapPin, label: "Zone", text: CONTACT.zone },
  ];
  const services = [
    { icon: Plane, text: "Transferts aéroport & gare" },
    { icon: Clock, text: "Mise à disposition" },
    { icon: Heart, text: "Véhicule de mariage" },
  ];
  return (
    <div
      className="relative w-[1050px] h-[600px] bg-[#F8F9FA] overflow-hidden flex"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* LEFT navy panel */}
      <div className="relative w-[360px] h-full bg-[#0B132B] overflow-hidden flex flex-col items-center justify-between py-12">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(#8ea2c8 1px, transparent 1px), linear-gradient(90deg, #8ea2c8 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="absolute -bottom-24 -left-16 w-[280px] h-[280px] rounded-full" style={{ background: "rgba(60,74,114,0.55)", filter: "blur(80px)" }} />

        <div className="relative z-10 flex flex-col items-center">
          <div className="w-[92px] h-[92px] rounded-full border border-white/15 flex items-center justify-center">
            <LogoMark size={64} />
          </div>
          <div className="mt-5 flex items-baseline gap-2">
            <span className="font-display text-white text-[40px] leading-none tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>OKA</span>
            <span className="text-[13px] font-bold tracking-[0.3em] text-[#0B132B] bg-white px-2 py-1">VTC</span>
          </div>
        </div>

        {/* QR */}
        <div className="relative z-10 flex flex-col items-center gap-3">
          <div className="bg-white p-3 rounded-xl">
            <QRCodeSVG value="https://okavtc.com/#booking" size={112} fgColor="#0B132B" bgColor="#ffffff" level="M" />
          </div>
          <span className="text-[12px] tracking-[0.3em] uppercase text-white/55">Scannez pour réserver</span>
        </div>
      </div>

      {/* RIGHT white content */}
      <div className="relative z-10 flex-1 px-12 py-11 flex flex-col justify-between">
        <div>
          <span className="text-[13px] tracking-[0.4em] uppercase text-[#8ea2c8] font-semibold">Chauffeur privé · Région lyonnaise</span>
          <h2 className="font-display text-[#0B132B] text-[38px] leading-tight tracking-tight mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Voyagez l'élégance,<br /><span className="italic text-[#3a4a72]">en toute sérénité</span>
          </h2>
        </div>

        {/* services */}
        <div className="flex gap-2.5">
          {services.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-start gap-2 border border-[#0B132B]/10 rounded-lg px-3.5 py-3">
              <s.icon className="w-5 h-5 text-[#3a4a72]" />
              <span className="text-[13px] font-semibold text-[#0B132B] leading-tight">{s.text}</span>
            </div>
          ))}
        </div>

        {/* contact grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3.5">
              <span className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#0B132B] text-white rounded-lg">
                <r.icon className="w-5 h-5" />
              </span>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#1C2541]/45">{r.label}</span>
                <span className="text-[18px] font-semibold text-[#0B132B] whitespace-nowrap">{r.text}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <span className="h-px flex-1 bg-[#0B132B]/10" />
          <span className="text-[12px] text-[#1C2541]/50 tracking-wide">Peugeot 508 SW Hybrid 180 Allure · HB-116-DS</span>
          <span className="h-px flex-1 bg-[#0B132B]/10" />
        </div>
      </div>
    </div>
  );
}

export default function BusinessCard() {
  useSEO({
    title: "Carte de visite — OKA VTC, chauffeur privé à Lyon",
    description: "Coordonnées et carte de visite numérique d'OKA VTC, chauffeur privé VTC à Lyon.",
  });

  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [loading, setLoading] = useState(null);

  const download = async (ref, name, key) => {
    if (!ref.current) return;
    setLoading(key);
    const el = ref.current;
    const prev = el.style.transform;
    el.style.transform = "scale(1)";
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      // warm-up render so web fonts are embedded on first capture
      await toPng(el, { pixelRatio: 1, cacheBust: true, width: 1050, height: 600 });
      const dataUrl = await toPng(el, { pixelRatio: 3, cacheBust: true, width: 1050, height: 600 });
      const link = document.createElement("a");
      link.download = name;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error(e);
    } finally {
      el.style.transform = prev;
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] grain">
      <div className="relative z-10 mx-auto max-w-[1200px] px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" data-testid="back-home" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour au site
          </Link>
          <span className="text-[11px] tracking-[0.3em] uppercase text-white/40">Carte de visite — OKA VTC</span>
        </div>

        <h1 className="font-display text-white text-4xl md:text-5xl tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
          Votre carte de visite
        </h1>
        <p className="text-white/60 mb-12 max-w-xl">Format standard 85×55 mm, export PNG haute résolution (300 dpi), prêt pour l'impression.</p>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
          {/* FRONT */}
          <div className="flex flex-col gap-5">
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#8ea2c8]">Recto</span>
            <div className="w-full overflow-hidden rounded-xl shadow-2xl shadow-black/40 border border-white/10">
              <div style={{ width: "100%", aspectRatio: "1050 / 600" }} className="relative">
                <div ref={frontRef} className="card-scale origin-top-left" style={{ transform: "scale(var(--s))", position: "absolute", top: 0, left: 0 }}>
                  <CardFront />
                </div>
              </div>
            </div>
            <button
              data-testid="download-front"
              onClick={() => download(frontRef, "OKA-VTC-carte-recto.png", "front")}
              className="flex items-center justify-center gap-2 bg-white text-[#0B132B] py-3.5 rounded-full font-semibold hover:bg-[#8ea2c8] transition-colors"
            >
              {loading === "front" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Télécharger le recto
            </button>
          </div>

          {/* BACK */}
          <div className="flex flex-col gap-5">
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#8ea2c8]">Verso</span>
            <div className="w-full overflow-hidden rounded-xl shadow-2xl shadow-black/40 border border-white/10">
              <div style={{ width: "100%", aspectRatio: "1050 / 600" }} className="relative">
                <div ref={backRef} className="card-scale origin-top-left" style={{ transform: "scale(var(--s))", position: "absolute", top: 0, left: 0 }}>
                  <CardBack />
                </div>
              </div>
            </div>
            <button
              data-testid="download-back"
              onClick={() => download(backRef, "OKA-VTC-carte-verso.png", "back")}
              className="flex items-center justify-center gap-2 bg-white text-[#0B132B] py-3.5 rounded-full font-semibold hover:bg-[#8ea2c8] transition-colors"
            >
              {loading === "back" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Télécharger le verso
            </button>
          </div>
        </div>
      </div>

      <ScaleController />
    </div>
  );
}

// Sets --s so the 1050px card scales to fit its responsive container width
function ScaleController() {
  useEffect(() => {
    const apply = () => {
      document.querySelectorAll(".card-scale").forEach((el) => {
        const parentW = el.parentElement.clientWidth;
        const s = parentW / 1050;
        el.style.setProperty("--s", s.toString());
        el.parentElement.style.height = 600 * s + "px";
      });
    };
    apply();
    const t = setTimeout(apply, 120);
    window.addEventListener("resize", apply);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", apply);
    };
  }, []);
  return null;
}
